import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { notification, Table, theme } from 'antd';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import {
  IData,
  IDataArea,
  EditedData,
} from 'store/ducks/DataEntry/ICTServices/types';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import useFilters from 'utils/hooks/useFilters';
import { globalOp } from 'store/ducks/Global';
import { functionOp } from 'store/ducks/DataEntry/Functions';
import { getEditableColumns } from 'components/Columns/DataTableColumns';
import { requiredFieldsForData } from 'constants/requiredFields';
import SaveButton from 'components/SaveButton/SaveButton';
import useTooltipClickOutside from 'utils/hooks/useTooltipClickOutside';
import { RootState } from 'store/configureStore';
import { article8Op } from 'store/ducks/DataManagement/Article8AndICTSystem';
import * as Styled from '../../../DataEntry/DataEntry.styled';
import { useTranslation } from 'react-i18next';

const Data: React.FC = () => {
  const { query } = useFilters();
  const { useToken } = theme;
  const { token } = useToken();
  const { t } = useTranslation();
  const topScrollRef = useRef<HTMLDivElement>(null);
  const topScrollInnerRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement | null>(null);
  const [data, setData] = useState<IDataArea[]>([]);
  const [editedData, setEditedData] = useState<EditedData>({});
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const [sensitivenessOfData, setSensitivenessOfData] = useState([]);
  const isLoggedIn = localStorage.getItem('authToken');
  const [isTooltipVisible, setIsTooltipVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const { countries } = useCountries();
  const { selectedSystem } = useSelector((state: RootState) => state.article8);

  const fetchData = async () => {
    try {
      if (!query.entityId) return;
      setData([]);

      let response;
      if (selectedSystem === 'ICT_SERVICE') {
        response = await article8Op.fetchICTSystemsByICTServices(
          query.entityId,
        );
      } else if (selectedSystem === 'FUNCTION') {
        response = await article8Op.fetchICTSystemsByFunctions(query.entityId);
      }

      const mappedData = response?.data?.ict_systems?.map((item: any) => ({
        key: `${query.entityId}-${selectedSystem}-${item.ict_system_id}`,
        ict_system: item.name,
        ict_system_id: item.ict_system_id,
        data:
          item.data?.map((iData: any) => ({
            sensitivenessOfDataStoredbyICTTPSP_id:
              iData.sensitiveness_of_data?.id,
            sensitivenessOfDataStoredbyICTTPSP_name:
              iData.sensitiveness_of_data?.name,
            data_id: iData.id,
            data_location_of_data_at_rest: iData.location_of_data_at_rest?.id,
            data_location_of_data_management:
              iData.location_of_data_management?.id,
            data_description: iData.description,
            data_ict_system_id: iData.ict_system_id,
            data_start_date: iData.start_date,
            data_end_date: iData.end_date,
            site: iData.site,
          })) ?? [],
      }));

      setData(mappedData || []);
    } catch (error) {
      console.log(error, 'error');
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [query.entityId, selectedSystem]);

  useEffect(() => {
    if (query.entityId) {
      fetchSensitivinessOfData();
    }
  }, [query.entityId]);

  const fetchSensitivinessOfData = async () => {
    try {
      const response = await globalOp.fetchSensitivenessOfData(query.entityId);
      if (response?.data.sensitiveness_of_data) {
        const formattedOptions = response.data.sensitiveness_of_data.map(
          (sensitiveness: { name: any; id: any }) => ({
            label: sensitiveness.name,
            value: sensitiveness.id,
          }),
        );
        setSensitivenessOfData(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const addNewData = (rowKey: string | number) => {
    const newData: IData = {
      sensitivenessOfDataStoredbyICTTPSP_id: '',
      sensitivenessOfDataStoredbyICTTPSP_name: '',
      data_id: generateUniqueId(),
      data_location_of_data_at_rest: '',
      data_location_of_data_management: '',
      data_description: '',
      data_start_date: '',
      data_end_date: '9999-01-01',
      site: '',
    };

    setData((prevData) => {
      return prevData.map((record) => {
        if (record.ict_system_id === rowKey) {
          return {
            ...record,
            data: [...record.data, newData],
          };
        }
        return record;
      });
    });
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    rowKey: string | number,
  ) => {
    const { value } = e.target;
    setEditedData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));

    const updatedRow = data.find(
      (row) => row.ict_system_id.toString() == rowKey,
    );
    if (updatedRow) {
      const updatedRowWithChanges: Record<string, any> = {
        ...updatedRow,
        [fieldName]: value,
      };
      if (updatedRow.data) {
        const match = fieldName.match(/^(\d+)_newItem_(.+)$/);
        if (match) {
          const idFromFieldName = match[1];
          const actualFieldName = match[2];
          const targetIndex = updatedRow.data.findIndex(
            (item) => item.data_id.toString() === idFromFieldName,
          );

          if (targetIndex !== -1) {
            updatedRowWithChanges.data[targetIndex] = {
              ...updatedRow.data[targetIndex],
              [actualFieldName]: value,
            };
          }
        }
      }
      const cleanedUpRow = Object.keys(updatedRowWithChanges).reduce(
        (acc: Record<string, any>, key) => {
          const newKey = key.replace(/^[^a-zA-Z]+/, '');
          acc[newKey] = updatedRowWithChanges[key];
          return acc;
        },
        {},
      );

      setChangedData((prevChangedData) => {
        const newChangedData = [...prevChangedData];
        const existingRowIndex = newChangedData.findIndex(
          (row) => row.ict_system_id === cleanedUpRow.ict_system_id,
        );

        if (existingRowIndex !== -1) {
          const existingRow = newChangedData[existingRowIndex];
          const mergedRow = { ...existingRow, ...cleanedUpRow };
          if (JSON.stringify(existingRow) !== JSON.stringify(mergedRow)) {
            newChangedData[existingRowIndex] = mergedRow;
          }
        } else {
          newChangedData.push(cleanedUpRow);
        }

        return newChangedData;
      });
    }
  };

  const updateChangedData = (editedData: any, changedData: any) => {
    if (editedData && typeof editedData === 'object') {
      Object.keys(editedData).forEach((key: string) => {
        const keyFromEditedData = parseInt(key.split('_')[0]);
        const matchingChangedRow = changedData.find((dataItem: any) => {
          return dataItem.ict_system_id === keyFromEditedData;
        });

        if (matchingChangedRow) {
          const fieldName = key.replace(/^\d+_/, '');
          if (matchingChangedRow.hasOwnProperty(fieldName)) {
            matchingChangedRow[fieldName] = editedData[key];
          }
        }
      });
    } else {
      console.log('editedData is not an object:', editedData);
    }

    setChangedData([...changedData]);
  };

  useEffect(() => {
    updateChangedData(editedData, changedData);
  }, [editedData]);

  const dataWithKey = data.map((item) => ({
    ...item,
    key: item.ict_system_id,
  }));
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '100'],
    position: ['topRight'] as ['topRight'],
  });

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize,
    });
  };

  const handleOpenTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: true }));
  };

  const handleCloseTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: false }));
  };
  useTooltipClickOutside({
    tooltipVisibility: isTooltipVisible,
    setTooltipVisibility: setIsTooltipVisible,
  });

  const handleSubmit = async () => {
    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description: 'There are no new or edited Datas to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const fieldKeyMap: Record<string, keyof IData> = {
      location_of_data_at_rest: 'data_location_of_data_at_rest',
      location_of_data_management: 'data_location_of_data_management',
      start_date: 'data_start_date',
      sensitiveness_of_data_stored_by_ict_tpsp_id:
        'sensitivenessOfDataStoredbyICTTPSP_id',
      description: 'data_description',
    };

    const hasEmptyFields = changedData.some((item) =>
      item.data.some((itemData: IData) =>
        requiredFieldsForData.some((logicalName) => {
          const actualKey = fieldKeyMap[logicalName];
          const value = itemData[actualKey];
          return (
            value === null || value === undefined || String(value).trim() === ''
          );
        }),
      ),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please make sure to enter a start date, location of data at rest, location of data management and sensitiveness of data store by ICT TPS fields before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      entity_name: query.entity,

      Data: changedData
        .map((item) =>
          item.data.map((itemData: IData) => ({
            id: itemData.data_id,
            location_of_data_at_rest: itemData.data_location_of_data_at_rest,
            location_of_data_management:
              itemData.data_location_of_data_management,
            description: itemData.data_description,
            start_date: itemData.data_start_date,
            end_date: itemData.data_end_date,
            ict_system_id: item.ict_system_id,
            sensitiveness_of_data_stored_by_ict_tpsp_id:
              itemData.sensitivenessOfDataStoredbyICTTPSP_id,
            site: itemData.site,
          })),
        )
        .flat(),
      token: isLoggedIn,
    };

    try {
      if (selectedSystem === 'ICT_SERVICE') {
        const response = await ict_serviceOp.postICTServices(formattedData);
      } else if (selectedSystem === 'FUNCTION') {
        const response = await functionOp.insertFunctions(formattedData);
      }
      setData((prev) => prev.map((item) => ({ ...item, isEdited: false })));
      setChangedData([]);
    } catch (error) {
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns = getEditableColumns({
    data,
    countries,
    sensitivenessOfData,
    editedData,
    isTooltipVisible,
    handleEditChange,
    handleOpenTooltip,
    handleCloseTooltip,
    setData,
    fetchData,
    addNewData,
    t,
  });

  useEffect(() => {
    const findScrollable = () => {
      const el = document.querySelector(
        '.ant-table-body, .ant-table-content',
      ) as HTMLDivElement | null;

      if (el) {
        tableContentRef.current = el;
      }
    };

    findScrollable();
    const observer = new MutationObserver(findScrollable);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const table = tableContentRef.current;

    if (!top || !table) return;

    const syncTop = () => {
      if (top) top.scrollLeft = table.scrollLeft;
    };
    const syncBottom = () => {
      if (table) table.scrollLeft = top.scrollLeft;
    };

    top.addEventListener('scroll', syncBottom);
    table.addEventListener('scroll', syncTop);

    return () => {
      top.removeEventListener('scroll', syncBottom);
      table.removeEventListener('scroll', syncTop);
    };
  }, [data, columns]);

  useEffect(() => {
    const table = tableContentRef.current;
    if (table && topScrollInnerRef.current) {
      topScrollInnerRef.current.style.width = `${table.scrollWidth}px`;
    }
  }, [data, columns]);

  return (
    <>
      <Styled.ButtonDiv isEmpty={dataWithKey.length === 0}></Styled.ButtonDiv>
      <Styled.TopScrollBar ref={topScrollRef}>
        <div ref={topScrollInnerRef} />
      </Styled.TopScrollBar>
      <Styled.TooltipTableContainer>
        <Table
          dataSource={dataWithKey}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: pagination.showSizeChanger,
            pageSizeOptions: pagination.pageSizeOptions,
            position: pagination.position,
          }}
          columns={columns}
        />
      </Styled.TooltipTableContainer>
      {dataWithKey.length > 0 && (
        <SaveButton
          token={token}
          hasChanges={changedData.length > 0}
          onClick={handleSubmit}
        />
      )}
    </>
  );
};

export default Data;
